/**
 * Link público — paciente responde questionário após login.
 *
 * Contrato de URL (hash router, ex.: `/#/signin?...`):
 *
 * **Novo (recomendado):** query `pq_ctx` — payload JSON codificado em Base64 **URL-safe**
 * (não é assinatura criptográfica; apenas ofusca/empacota dados e dificulta alteração casual).
 * Versão atual: `v === 1` com `questionnaireId`, `practitionerRoleReference`, `ownerOrganizationReference`.
 *
 * **Legado (transição):** parâmetro `questionnarie` (grafia mantida em produção).
 *   Valores aceitos: `Questionnaire/<id>` ou apenas `<id>`.
 *   Alias opcional: `questionnaire` (lido só se `questionnarie` estiver ausente).
 *   Links legados não carregam contexto de notificação; o paciente responde sem criar task de
 *   “questionário respondido” para o profissional (comportamento anterior).
 *
 * Armazenamento:
 * - `fluxmed_pending_questionnaire`: id normalizado do Questionnaire (sessionStorage), consumido no redirect pós-login.
 * - `fluxmed_patient_questionnaire_link_notifier`: JSON com refs FHIR para PostTask após resposta (fluxo com `pq_ctx`);
 *   validado contra o id da rota em `/patient-questionnaire/:id`; removido com a flag de fluxo.
 * - `fluxmed_patient_questionnaire_link_flow`: flag "1" enquanto o fluxo veio do link; evita ViewDialog de clínica
 *   até conclusão ou abandono; limpar em sucesso no envio ou ao sair explicitamente.
 */

export const PENDING_QUESTIONNAIRE_SESSION_KEY = "fluxmed_pending_questionnaire";

/**
 * Contexto mínimo para replicar `QuestionnaireRespondPage` / PostTask quando não há task prévia (fluxo link).
 */
export const PATIENT_QUESTIONNAIRE_LINK_NOTIFIER_SESSION_KEY =
  "fluxmed_patient_questionnaire_link_notifier";

/** Valor truthy em sessionStorage: fluxo iniciado por link de questionário (visão paciente forçada até limpar). */
export const PATIENT_QUESTIONNAIRE_LINK_FLOW_SESSION_KEY =
  "fluxmed_patient_questionnaire_link_flow";

/** Nome do query param usado em links antigos / produção. */
export const QUESTIONNAIRE_SIGNIN_QUERY_PARAM_LEGACY = "questionnarie";

/** Alias opcional (somente se o legado não vier na URL). */
export const QUESTIONNAIRE_SIGNIN_QUERY_PARAM_ALIAS = "questionnaire";

/**
 * Payload compacto na URL (Base64 URL-safe). Documentar mudanças de formato aqui e em `PATIENT_QUESTIONNAIRE_SIGNIN_PAYLOAD_VERSION`.
 */
export const QUESTIONNAIRE_SIGNIN_QUERY_PARAM_CONTEXT = "pq_ctx";

/** Versão do objeto JSON dentro de `pq_ctx` (incrementar só com migrador ou leitura dual). */
export const PATIENT_QUESTIONNAIRE_SIGNIN_PAYLOAD_VERSION = 1 as const;

const FLOW_FLAG_ACTIVE_VALUE = "1";

export interface PatientQuestionnaireSigninPayloadV1 {
  v: typeof PATIENT_QUESTIONNAIRE_SIGNIN_PAYLOAD_VERSION;
  questionnaireId: string;
  /** Ex.: `PractitionerRole/<orgId>-<oids.cpf>-<username>` */
  practitionerRoleReference: string;
  /** Ex.: `Organization/<oids.cnpj>-<organizationId>` */
  ownerOrganizationReference: string;
}

export interface PatientQuestionnaireLinkNotifierContext {
  questionnaireId: string;
  practitionerRoleReference: string;
  ownerOrganizationReference: string;
}

export interface BuildPatientQuestionnaireSigninLinkContext {
  practitionerRoleReference: string;
  ownerOrganizationReference: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function encodeJsonToBase64Url(payload: PatientQuestionnaireSigninPayloadV1): string {
  const json = JSON.stringify(payload);
  const utf8Bytes = new TextEncoder().encode(json);
  let binary = "";
  utf8Bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeBase64UrlToString(encoded: string): string | null {
  try {
    let padded = encoded.replace(/-/g, "+").replace(/_/g, "/");
    while (padded.length % 4) padded += "=";
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return null;
  }
}

function parsePayloadV1(data: unknown): PatientQuestionnaireSigninPayloadV1 | null {
  if (!data || typeof data !== "object") return null;
  const o = data as Record<string, unknown>;
  if (o.v !== PATIENT_QUESTIONNAIRE_SIGNIN_PAYLOAD_VERSION) return null;
  if (
    !isNonEmptyString(o.questionnaireId) ||
    !isNonEmptyString(o.practitionerRoleReference) ||
    !isNonEmptyString(o.ownerOrganizationReference)
  ) {
    return null;
  }
  const pr = o.practitionerRoleReference.trim();
  const orgRef = o.ownerOrganizationReference.trim();
  if (!pr.startsWith("PractitionerRole/") || !orgRef.startsWith("Organization/")) {
    return null;
  }
  return {
    v: PATIENT_QUESTIONNAIRE_SIGNIN_PAYLOAD_VERSION,
    questionnaireId: o.questionnaireId.trim(),
    practitionerRoleReference: pr,
    ownerOrganizationReference: orgRef,
  };
}

/**
 * Decodifica e valida `pq_ctx` da URL. Retorna null se inválido.
 */
export function tryParsePatientQuestionnaireSigninPayloadFromBase64Url(
  rawEncoded: string,
): PatientQuestionnaireSigninPayloadV1 | null {
  const trimmed = rawEncoded.trim();
  if (!trimmed) return null;
  const json = decodeBase64UrlToString(trimmed);
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    return parsePayloadV1(parsed);
  } catch {
    return null;
  }
}

function getQueryParamFromHref(paramName: string): string | null {
  try {
    const currentUrl = window.location.href;
    const patterns = [
      `[?&]${paramName}=([^&#]*)`,
      `#/?${paramName}=([^&#]*)`,
      `#?${paramName}=([^&#]*)`,
    ];
    for (const pattern of patterns) {
      const match = new RegExp(pattern).exec(currentUrl);
      if (match?.[1]) {
        return decodeURIComponent(match[1]);
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Remove um parâmetro da URL atual sem recarregar (paridade com `urlParamManager.removeUrlParam`).
 */
function removeQueryParamFromCurrentUrl(paramName: string): void {
  try {
    let newUrl = window.location.href;
    const valuePattern = "=[^&]*";
    const patterns = [
      `\\?${paramName}${valuePattern}&`,
      `&${paramName}${valuePattern}&`,
      `\\?${paramName}${valuePattern}`,
      `&${paramName}${valuePattern}`,
    ];
    patterns.forEach((pattern) => {
      const regex = new RegExp(pattern);
      if (regex.test(newUrl)) {
        if (pattern === `\\?${paramName}${valuePattern}&`) {
          newUrl = newUrl.replace(regex, "?");
        } else if (pattern === `&${paramName}${valuePattern}&`) {
          newUrl = newUrl.replace(regex, "&");
        } else {
          newUrl = newUrl.replace(regex, "");
        }
      }
    });
    window.history.replaceState({}, document.title, newUrl);
  } catch {
    /* fallback silencioso */
  }
}

/**
 * Normaliza o valor bruto do query param legado para o id lógico do Questionnaire.
 */
export function normalizeQuestionnaireIdFromSigninParam(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("Questionnaire/")) {
    const idPart = trimmed.split("/")[1]?.trim();
    return idPart || null;
  }
  return trimmed;
}

function persistNotifierContext(ctx: PatientQuestionnaireLinkNotifierContext): void {
  try {
    sessionStorage.setItem(
      PATIENT_QUESTIONNAIRE_LINK_NOTIFIER_SESSION_KEY,
      JSON.stringify(ctx),
    );
  } catch {
    /* ignore */
  }
}

export function clearPatientQuestionnaireLinkNotifierContext(): void {
  try {
    sessionStorage.removeItem(PATIENT_QUESTIONNAIRE_LINK_NOTIFIER_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function persistPendingQuestionnaireAndActivateLinkFlow(
  questionnaireId: string,
): void {
  try {
    sessionStorage.setItem(PENDING_QUESTIONNAIRE_SESSION_KEY, questionnaireId);
    sessionStorage.setItem(
      PATIENT_QUESTIONNAIRE_LINK_FLOW_SESSION_KEY,
      FLOW_FLAG_ACTIVE_VALUE,
    );
  } catch {
    /* ignore */
  }
}

/**
 * Persiste questionário + contexto de notificação (fluxo `pq_ctx`).
 */
export function persistPendingQuestionnaireLinkWithNotifierContext(
  payload: PatientQuestionnaireSigninPayloadV1,
): void {
  persistPendingQuestionnaireAndActivateLinkFlow(payload.questionnaireId);
  persistNotifierContext({
    questionnaireId: payload.questionnaireId,
    practitionerRoleReference: payload.practitionerRoleReference,
    ownerOrganizationReference: payload.ownerOrganizationReference,
  });
}

/**
 * Lê o contexto de notificação sem remover (ex.: validar id da rota).
 */
export function peekPatientQuestionnaireLinkNotifierContext(): PatientQuestionnaireLinkNotifierContext | null {
  try {
    const raw = sessionStorage.getItem(PATIENT_QUESTIONNAIRE_LINK_NOTIFIER_SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const o = parsed as Record<string, unknown>;
    if (
      !isNonEmptyString(o.questionnaireId) ||
      !isNonEmptyString(o.practitionerRoleReference) ||
      !isNonEmptyString(o.ownerOrganizationReference)
    ) {
      return null;
    }
    return {
      questionnaireId: o.questionnaireId.trim(),
      practitionerRoleReference: o.practitionerRoleReference.trim(),
      ownerOrganizationReference: o.ownerOrganizationReference.trim(),
    };
  } catch {
    return null;
  }
}

/**
 * Lê e remove o contexto de notificação (ex.: após enviar PostTask com sucesso).
 */
export function consumePatientQuestionnaireLinkNotifierContext(): PatientQuestionnaireLinkNotifierContext | null {
  const ctx = peekPatientQuestionnaireLinkNotifierContext();
  clearPatientQuestionnaireLinkNotifierContext();
  return ctx;
}

/**
 * Lê `pq_ctx` primeiro; senão `questionnarie` / `questionnaire`. Persiste pending + flag de fluxo;
 * com `pq_ctx`, persiste também notifier context; com legado, remove notifier antigo para evitar mistura.
 * Remove os params da barra após sucesso.
 */
export function tryIngestQuestionnaireParamFromCurrentUrl(): boolean {
  try {
    const rawCtx = getQueryParamFromHref(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_CONTEXT);
    if (rawCtx) {
      const payload = tryParsePatientQuestionnaireSigninPayloadFromBase64Url(rawCtx);
      if (payload) {
        persistPendingQuestionnaireLinkWithNotifierContext(payload);
        removeQueryParamFromCurrentUrl(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_CONTEXT);
        removeQueryParamFromCurrentUrl(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_LEGACY);
        removeQueryParamFromCurrentUrl(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_ALIAS);
        return true;
      }
    }

    /**
     * Links só com `questionnarie` / `questionnaire` permanecem suportados (transição).
     * Não disparam `pq_ctx`: sem contexto de profissional no sessionStorage, o portal não cria
     * a task QUESTIONNAIRE_ANSWERED_NOTIFICATION após a resposta. Novos links pela gestão usam `pq_ctx`.
     */
    const tryPersistLegacy = (raw: string): boolean => {
      const id = normalizeQuestionnaireIdFromSigninParam(raw);
      if (!id) return false;
      clearPatientQuestionnaireLinkNotifierContext();
      persistPendingQuestionnaireAndActivateLinkFlow(id);
      removeQueryParamFromCurrentUrl(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_LEGACY);
      removeQueryParamFromCurrentUrl(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_ALIAS);
      return true;
    };

    const rawLegacy = getQueryParamFromHref(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_LEGACY);
    if (rawLegacy && tryPersistLegacy(rawLegacy)) return true;

    const rawAlias = getQueryParamFromHref(QUESTIONNAIRE_SIGNIN_QUERY_PARAM_ALIAS);
    if (rawAlias && tryPersistLegacy(rawAlias)) return true;

    return false;
  } catch {
    return false;
  }
}

export function peekPendingQuestionnaireId(): string | null {
  try {
    return sessionStorage.getItem(PENDING_QUESTIONNAIRE_SESSION_KEY);
  } catch {
    return null;
  }
}

/** Lê e remove só o pending (mantém a flag de fluxo até conclusão/abandono). */
export function consumePendingQuestionnaireIdFromSession(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_QUESTIONNAIRE_SESSION_KEY);
    if (id) sessionStorage.removeItem(PENDING_QUESTIONNAIRE_SESSION_KEY);
    return id;
  } catch {
    return null;
  }
}

export function isPatientQuestionnaireLinkFlowActive(): boolean {
  try {
    return (
      sessionStorage.getItem(PATIENT_QUESTIONNAIRE_LINK_FLOW_SESSION_KEY) ===
      FLOW_FLAG_ACTIVE_VALUE
    );
  } catch {
    return false;
  }
}

export function clearPatientQuestionnaireLinkFlow(): void {
  try {
    sessionStorage.removeItem(PATIENT_QUESTIONNAIRE_LINK_FLOW_SESSION_KEY);
    clearPatientQuestionnaireLinkNotifierContext();
  } catch {
    /* ignore */
  }
}

/**
 * Após login bem-sucedido: pending → `/patient-questionnaire/:id`; senão destino padrão.
 */
export function navigateAfterSigninWithOptionalQuestionnaire(
  navigate: (to: string, options?: { replace?: boolean }) => void,
  defaultPath = "/",
): void {
  const id = consumePendingQuestionnaireIdFromSession();
  if (id) {
    navigate(`/patient-questionnaire/${id}`, { replace: true });
    return;
  }
  navigate(defaultPath, { replace: true });
}

export interface BuildPatientQuestionnaireSigninLinkOptions {
  /**
   * Quando informado, gera `pq_ctx` (Base64 URL-safe) com questionário + refs FHIR
   * no mesmo padrão das tasks de envio (`questionnaireTaskWorkflowService` / diálogo de envio).
   */
  notifierContext?: BuildPatientQuestionnaireSigninLinkContext;
}

/**
 * Monta o payload JSON v1 e codifica para `pq_ctx`.
 */
export function buildPatientQuestionnaireSigninPayloadBase64Url(
  questionnaireId: string,
  notifier: BuildPatientQuestionnaireSigninLinkContext,
): string {
  const trimmedId = questionnaireId.trim();
  const payload: PatientQuestionnaireSigninPayloadV1 = {
    v: PATIENT_QUESTIONNAIRE_SIGNIN_PAYLOAD_VERSION,
    questionnaireId: trimmedId,
    practitionerRoleReference: notifier.practitionerRoleReference.trim(),
    ownerOrganizationReference: notifier.ownerOrganizationReference.trim(),
  };
  return encodeJsonToBase64Url(payload);
}

/**
 * URL completa para o paciente (hash + signin).
 * Com `notifierContext`, usa apenas `pq_ctx` (sem duplicar questionnaire na query).
 * Sem contexto, mantém o param legado `questionnarie` para compatibilidade com links antigos gerados pela UI.
 */
export function buildPatientQuestionnaireSigninLink(
  questionnaireId: string,
  options?: BuildPatientQuestionnaireSigninLinkOptions,
): string {
  const trimmedId = questionnaireId.trim();
  const origin = window.location.origin;
  const pathname = window.location.pathname.replace(/\/$/, "");

  let hashPathAndQuery: string;
  if (options?.notifierContext) {
    const ctx = buildPatientQuestionnaireSigninPayloadBase64Url(
      trimmedId,
      options.notifierContext,
    );
    hashPathAndQuery = `/#/signin?${QUESTIONNAIRE_SIGNIN_QUERY_PARAM_CONTEXT}=${encodeURIComponent(ctx)}`;
  } else {
    /** `/#/…` evita `…/portal#/signin` (hash colado no segmento); o correto é `…/portal/#/signin`. */
    hashPathAndQuery = `/#/signin?${QUESTIONNAIRE_SIGNIN_QUERY_PARAM_LEGACY}=${encodeURIComponent(
      `Questionnaire/${trimmedId}`,
    )}`;
  }

  return `${origin}${pathname}${hashPathAndQuery}`;
}
